import { ITransport } from '@epicgames-ps/lib-pixelstreamingcommon-ue5.5'
import {
  Config,
  PixelStreaming,
  WebRtcPlayerController,
} from '@epicgames-ps/lib-pixelstreamingfrontend-ue5.5'
import { useRuntimeVariables } from 'app-kit'
import { useGeckoAuth } from 'features/auth'
import { useRolodexEdit } from 'features/fde'
import type { KestrelViewerMessageCallbackAPI } from 'features/kestrelViewer'
import {
  calculateInspectableSqft,
  cleanupMetricsStorage,
  createSessionMetrics,
  endKestrelSession,
  getAllMessages,
  hasActiveSession,
  initializeMetricsStorage,
  KestrelViewerProvider,
  KestrelViewerUI,
  saveSessionMetricsToRolodex,
  startKestrelSession,
  startWebRTCStatsCollection,
  updateMarkup,
  useCameraPoses,
  useKestrel,
  usePhotoFiles,
  useWhiteboardId,
} from 'features/kestrelViewer'
import { PhotoReelCarousel } from 'features/kestrelViewer/ui/tools/photoreel/PhotoReelCarousel'
import { useGeckoPermissions } from 'features/permissions'
import React, {
  FC,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import styled from 'styled-components'
import { Vector2 } from 'three'
import {
  KVAnnotation,
  KVCameraPose,
  KVInspectionGrid,
  KVMeasurement,
  KVNavigationMode,
  KVPhoto,
  KVPosition,
  KVResolutionQuality,
  KVRotation,
  KVScreenshotResult,
  KVUnitType,
  KVUserSettings,
} from 'types'

import { useAnnotationCounters } from './hooks/useAnnotationCounters'
import { useDataLayerManagement } from './hooks/useDataLayerManagement'
import { useHistoricalAnnotations } from './hooks/useHistoricalAnnotations'
import { useInspectionAreaWorkOrders } from './hooks/useInspectionAreaWorkOrders'
import { useStreamResolution } from './hooks/useStreamResolution'
import { useTagOptions } from './hooks/useTagOptions'
import { useTemperatureProbe } from './hooks/useTemperatureProbe'
import { KestrelTutorial } from './tutorial/KestrelTutorial'
import { useKestrelTutorialContext } from './tutorial/KestrelTutorialContext'
import { KestrelCommands } from './types/KestrelCommands'
import { initializationCommands } from './utils/initializationCommands'
import type {
  ChunkBuffer,
  MessageHandler,
  ParsedMessage,
} from './utils/messageUtils'
import {
  emitPixelStreamUIInteraction,
  handleMessageWithHandlers,
  parseMessage,
  processChunkedMessage,
} from './utils/messageUtils'

// Extended types for monkey-patching the pixel streaming internals.
// The library doesn't export these internal methods, so we extend the types.
interface WebRtcControllerWithPrev extends WebRtcPlayerController {
  prevOnMessage?: (event: MessageEvent) => void
}

interface WebSocketTransportInternal extends ITransport {
  handleOnOpen: () => void
  handleOnMessage: (event: MessageEvent) => void
}

const HARD_RESET_TIMEOUT_MS = 10000

const Container = styled.div`
  width: 100%;
  height: 100%;
  position: relative;
`

// Component that attempts to connect to a pixel streaming server once a url is available
export const PixelStreamUI: FC = () => {
  const { showTutorial } = useKestrelTutorialContext()
  const { authorization } = useGeckoAuth()
  const { user = { name: '', email: '' } } = useGeckoPermissions()
  const { rolodexPatch } = useRolodexEdit()
  const { KESTREL_API_URL, ROLODEX_URL } = useRuntimeVariables()
  const selectedWhiteboardId = useWhiteboardId() // Get whiteboard ID from SystemView context
  const {
    authorizedAxios,
    pixelStreamingURL,
    setPixelStreamingURL,
    devMode,
    kestrelStatus,
    setVideoReady,
    startLoadingSequence,
    setLoadingStep,
    setLoading,
    setHasTimedOut,
    setVmDeleted,
    setVmID,
    setError,
    setResetting,
    selectedMarkup,
    setSelectedMarkup,
    setSendKVCommand,
    sendKVCommand,
    popupMenuStartPosition,
    setPopupMenuStartPosition,
    kestrelModel,
    inspectionType,
    annotations,
    setAnnotations,
    historicalAnnotations,
    setHistoricalAnnotations,
    measurements,
    setMeasurements,
    planeIntersections,
    setPlaneIntersections,
    cameraViews,
    setCameraViews,
    inspectionAreas,
    setInspectionAreas,
    updateInspectionArea,
    primitives,
    setPrimitives,
    updatePrimitive,
    setTagOptions,
    setValueOverrides,
    setDerivedTags,
    gatekeeper,
  } = useKestrel()

  // Load photos for the kestrel model
  const {
    photos: serverPhotos,
    isLoading: photosLoading,
    error: photosError,
  } = usePhotoFiles(
    kestrelModel?.data.photosEnabled ? kestrelModel.id : undefined
  )

  const { cameraPoses: cameraPosesFromRolodex } = useCameraPoses(
    kestrelModel?.id
  )

  const containerRef = useRef<HTMLDivElement>(null)
  const videoParent = useRef<HTMLDivElement>(null)
  const [pixelStreaming, setPixelStreaming] = useState<PixelStreaming>()
  const [selectedMode, setSelectedMode] = useState('cursor')
  const [measurementType, setMeasurementType] = useState('straightLine')
  const mouseTrackerRef = useRef<Vector2>(new Vector2(0, 0))
  const [helperTextVisible, setHelperTextVisible] = useState(false)
  const [settingsOpen, setSettingsOpen] = useState(false)
  const [infoOpen, setInfoOpen] = useState(false)
  const [inspectionGridSettingsOpen, setInspectionGridSettingsOpen] =
    useState(false)
  const [textSegmentationPopoverVisible, setTextSegmentationPopoverVisible] =
    useState(false)
  const [settings, setSettings] = useState<KVUserSettings>(() => {
    try {
      const stored = localStorage.getItem('kestrelViewer_settings')
      if (stored) {
        const parsed = JSON.parse(stored)
        const settings = KVUserSettings.fromJSON(parsed)
        return settings
      }
    } catch (error) {
      console.warn('Failed to load settings from localStorage:', error)
    }
    return new KVUserSettings()
  })
  const [inspectionGridSettings, setInspectionGridSettings] =
    useState<KVInspectionGrid>()
  const [is2DPlaneModeActive, setIs2DPlaneModeActive] = useState(false)

  useEffect(() => {
    try {
      localStorage.setItem('kestrelViewer_settings', JSON.stringify(settings))
    } catch (error) {
      console.warn('Failed to save settings to localStorage:', error)
    }
  }, [settings])
  const [planePosition, setPlanePosition] = useState<KVPosition>()
  const [planeRotation, setPlaneRotation] = useState<KVRotation>()
  const [photoReelOpen, setPhotoReelOpen] = useState(false)
  const [photos, setPhotos] = useState<KVPhoto[]>([])
  const [selectedPhoto, setSelectedPhoto] = useState<KVPhoto | null>(null)
  const [filteredPhotos, setFilteredPhotos] = useState<KVPhoto[] | null>(null)
  const [photoRotations, setPhotoRotations] = useState<Record<string, number>>(
    {}
  )

  const getPhotoRotation = useCallback(
    (photoId: string) => photoRotations[photoId] ?? 0,
    [photoRotations]
  )

  const setPhotoRotation = useCallback((photoId: string, rotation: number) => {
    setPhotoRotations((prev) => ({
      ...prev,
      [photoId]: rotation,
    }))
  }, [])

  const savePhotoRotations = useCallback(async () => {
    if (!kestrelModel?.id) return

    // Normalize rotations to 0-360 and filter out zero rotations
    const normalizeRotation = (r: number) => r % 360
    const normalizedRotations = Object.fromEntries(
      Object.entries(photoRotations)
        .map(([k, v]) => [k, normalizeRotation(v)])
        .filter(([, v]) => v !== 0)
    )

    // Only save if there are rotations to save or if we need to clear existing ones
    const hasExistingRotations =
      kestrelModel.data.photoRotations &&
      Object.keys(kestrelModel.data.photoRotations).length > 0
    const hasNewRotations = Object.keys(normalizedRotations).length > 0

    if (hasNewRotations || hasExistingRotations) {
      await rolodexPatch([
        {
          id: kestrelModel.id,
          data: { photoRotations: normalizedRotations },
        },
      ])
    }
  }, [kestrelModel, photoRotations, rolodexPatch])
  const [navigationMode, setNavigationMode] =
    useState<KVNavigationMode>('orbit')
  const [cameraPoses, setCameraPoses] = useState<KVCameraPose[] | null>([])
  const [filteringPhotos, setFilteringPhotos] = useState(false)
  const [thermalGradientVisible, setThermalGradientVisible] = useState(false)
  const [dataValidationPanelOpen, setDataValidationPanelOpen] = useState(false)
  const [resolutionQuality, setResolutionQuality] =
    useState<KVResolutionQuality>(KVResolutionQuality.High)
  const textSegmentationCompleteCallbackRef = useRef<(() => void) | undefined>(
    undefined
  )
  // Custom hooks for data layer management
  const {
    activeLayer,
    setActiveLayer,
    availableLayerPrefixes,
    setAvailableLayerPrefixes,
    currentTemperature,
    setCurrentTemperature,
    handleLayerSwitch,
    emitCurrentLayerCommand,
    hasDataLayersSupport,
  } = useDataLayerManagement({ kestrelModel, pixelStreaming })

  const {
    temperatureProbeLoading,
    setTemperatureProbeLoading,
    handleTemperatureProbe,
  } = useTemperatureProbe({ pixelStreaming, selectedMode, gatekeeper })

  useStreamResolution(
    pixelStreaming,
    videoParent,
    resolutionQuality,
    kestrelStatus?.videoReady ?? false
  )

  const [screenshotResult, setScreenshotResult] =
    useState<KVScreenshotResult | null>(null)

  // Message buffer for debug tools
  const [recentMessages, setRecentMessages] = useState<ParsedMessage[]>([])

  const addMessage = useCallback((message: ParsedMessage) => {
    // Filter out noisy control messages that don't provide value
    if (message.type === 'control' || message.type === 'confirmId') {
      return
    }

    setRecentMessages((prev) => {
      const newMessages = [message, ...prev]
      if (newMessages.length > 100) {
        return newMessages.slice(0, 100)
      }
      return newMessages
    })
  }, [])

  // Track whether streamers are available
  const [streamersAvailable, setStreamersAvailable] = useState(false)
  const hardResetTimeoutRef = useRef<NodeJS.Timeout | null>(null)
  const loadingSequenceRef = useRef<number | null>(null)

  // Add current unit state
  const getFallbackUnit = () => gatekeeper.fallbackUnit
  const initialUnit = kestrelModel?.data?.defaultUnit ?? getFallbackUnit()
  const [currentUnit, setCurrentUnit] = useState<KVUnitType>(
    initialUnit as KVUnitType
  )

  // Counter management for generating unique labels
  const {
    nextAnnotationNumber,
    setNextAnnotationNumber,
    nextMeasurementNumber,
    setNextMeasurementNumber,
  } = useAnnotationCounters(annotations, measurements, kestrelModel?.id)

  // Get work order counts for inspection areas
  const { workOrdersMap } = useInspectionAreaWorkOrders(
    kestrelModel?.id,
    annotations,
    inspectionAreas
  )

  // Sync work order counts to inspection areas and send to Unreal
  useEffect(() => {
    if (!inspectionAreas) return

    const changes: Array<{ id: string; count: number }> = []
    const updated = inspectionAreas.map((ia) => {
      const count = workOrdersMap[ia.id]?.length ?? 0
      if (ia.workOrderCount !== count) {
        changes.push({ id: ia.id, count })
        return { ...ia, workOrderCount: count }
      }
      return ia
    })

    if (changes.length > 0) {
      setInspectionAreas(updated)
      // Send updates to Unreal if video is ready
      if (sendKVCommand && kestrelStatus?.videoReady) {
        changes.forEach(({ id, count }) => {
          sendKVCommand({
            command: KestrelCommands.UPDATE_INSPECTION_AREA,
            inspectionAreaId: id,
            inspectionAreaWorkOrderCount: count,
          })
        })
      }
    }
  }, [
    workOrdersMap,
    inspectionAreas,
    setInspectionAreas,
    sendKVCommand,
    kestrelStatus?.videoReady,
  ])

  // Sync local photos state with server photos
  useEffect(() => {
    setPhotos(serverPhotos)
  }, [serverPhotos])

  // Sync local camera poses state with camera poses from rolodex
  useEffect(() => {
    setCameraPoses(cameraPosesFromRolodex)
  }, [cameraPosesFromRolodex])

  const messageHandlers = useMemo(() => {
    return getAllMessages()
  }, [])

  const kestrelViewerAPI = useMemo(() => {
    return {
      pixelStream: pixelStreaming,
      user: { name: user.name, email: user.email },
      videoParent,
      setSelectedMarkup,
      mouseTrackerRef: mouseTrackerRef,
      setPopupMenuStartPosition,
      annotations,
      setAnnotations,
      historicalAnnotations,
      setHistoricalAnnotations,
      inspectionGridSettings,
      setInspectionGridSettings,
      settings,
      setSettings,
      measurements,
      setMeasurements,
      cameraViews,
      setCameraViews,
      navigationMode,
      setNavigationMode,
      rolodexPatch,
      kestrelModel,
      planePosition,
      setPlanePosition,
      planeRotation,
      setPlaneRotation,
      planeIntersections,
      setPlaneIntersections,
      photoReelOpen,
      setPhotoReelOpen,
      selectedPhoto,
      setSelectedPhoto,
      photos,
      setPhotos,
      filteredPhotos,
      setFilteredPhotos,
      authorizedAxios,
      kestrelApiUrl: KESTREL_API_URL ?? '',
      cameraPoses,
      setCameraPoses,
      filteringPhotos,
      setFilteringPhotos,
      currentTemperature,
      setCurrentTemperature,
      temperatureProbeLoading,
      setTemperatureProbeLoading,
      selectedMode,
      setScreenshotResult,
      currentUnit,
      setCurrentUnit,
      nextAnnotationNumber,
      setNextAnnotationNumber,
      nextMeasurementNumber,
      setNextMeasurementNumber,
      inspectionAreas,
      setInspectionAreas,
      primitives,
      setPrimitives,
      resolutionQuality,
      setResolutionQuality,
      selectedWhiteboardId, // Inject whiteboard ID from SystemView context
      onTextSegmentationComplete: () => {
        textSegmentationCompleteCallbackRef.current?.()
      },
    } as KestrelViewerMessageCallbackAPI
  }, [
    pixelStreaming,
    user.name,
    user.email,
    videoParent,
    setSelectedMarkup,
    mouseTrackerRef,
    setPopupMenuStartPosition,
    annotations,
    setAnnotations,
    historicalAnnotations,
    setHistoricalAnnotations,
    inspectionGridSettings,
    setInspectionGridSettings,
    settings,
    setSettings,
    measurements,
    setMeasurements,
    cameraViews,
    setCameraViews,
    navigationMode,
    setNavigationMode,
    rolodexPatch,
    kestrelModel,
    planePosition,
    setPlanePosition,
    planeRotation,
    setPlaneRotation,
    planeIntersections,
    setPlaneIntersections,
    photoReelOpen,
    setPhotoReelOpen,
    selectedPhoto,
    setSelectedPhoto,
    photos,
    setPhotos,
    filteredPhotos,
    setFilteredPhotos,
    authorizedAxios,
    KESTREL_API_URL,
    cameraPoses,
    setCameraPoses,
    filteringPhotos,
    setFilteringPhotos,
    currentTemperature,
    setCurrentTemperature,
    temperatureProbeLoading,
    setTemperatureProbeLoading,
    selectedMode,
    selectedWhiteboardId,
    setScreenshotResult,
    currentUnit,
    setCurrentUnit,
    nextAnnotationNumber,
    setNextAnnotationNumber,
    nextMeasurementNumber,
    setNextMeasurementNumber,
    inspectionAreas,
    setInspectionAreas,
    primitives,
    setPrimitives,
    resolutionQuality,
    setResolutionQuality,
  ])

  const webSocketHandlerMonkeyPatchedRef = useRef(false)

  useEffect(() => {
    if (!(pixelStreamingURL && videoParent.current)) {
      return
    }

    // Remove any existing children created by a previous `PixelStreaming` instance
    while (videoParent.current.firstChild) {
      videoParent.current.removeChild(videoParent.current.firstChild)
    }

    const defaultSettings = {
      AutoPlayVideo: true,
      AutoConnect: false, // Changed to false - we'll connect manually after patching handlers
      ss: pixelStreamingURL,
      StartVideoMuted: true,
      HoveringMouse: true,
      WaitForStreamer: true,
    }
    const config = new Config({ initialSettings: defaultSettings })
    const pixelStream = new PixelStreaming(config, {
      videoElementParent: videoParent.current,
    })

    // Create stable `prevOnMessage` on pixel streaming creation that we can
    // call from a monkeypatched `handleOnMessage` that will be updated more
    // frequently later
    const controller = pixelStream.webRtcController as WebRtcControllerWithPrev
    controller.prevOnMessage = controller.handleOnMessage.bind(controller)

    setPixelStreaming(pixelStream)
    webSocketHandlerMonkeyPatchedRef.current = false

    return () => {
      pixelStream.disconnect()
    }
  }, [pixelStreamingURL])

  useEffect(() => {
    if (!pixelStreaming) {
      // Reset video readiness before (re)connecting
      setVideoReady(false)
      return
    }

    const mouseListener = (e: MouseEvent) => {
      if (mouseTrackerRef.current) {
        mouseTrackerRef.current.x = e.clientX
        mouseTrackerRef.current.y = e.clientY
      }
    }
    document.body.addEventListener('mousemove', mouseListener)

    const clearHardResetTimeout = () => {
      if (hardResetTimeoutRef.current) {
        clearTimeout(hardResetTimeoutRef.current)
        hardResetTimeoutRef.current = null
      }
    }

    const requestNewStreamingSession = () => {
      console.warn('Requesting new streaming session after timeout...')

      // End current session before starting a new one (if one exists)
      if (hasActiveSession()) {
        endKestrelSession()
      }

      clearHardResetTimeout()
      setStreamersAvailable(false)
      setResetting(true)
      setLoading(true)
      setHasTimedOut(false)
      setVmDeleted(false)
      setError(null)
      loadingSequenceRef.current = startLoadingSequence()
      setLoadingStep('reserving-vm', loadingSequenceRef.current)

      try {
        pixelStreaming.disconnect()
      } catch (error) {
        console.warn('Failed to disconnect pixel stream during reset:', error)
      }

      setPixelStreamingURL('')
      setVmID(null)
    }

    const startHardResetTimeout = () => {
      clearHardResetTimeout()
      hardResetTimeoutRef.current = setTimeout(() => {
        // TODO: Make sure `streamersAvailable` is updated and not pointing at an old object
        if (!streamersAvailable) {
          console.warn(
            `No streamer available after ${HARD_RESET_TIMEOUT_MS}ms, requesting new VM...`
          )
          requestNewStreamingSession()
        }
      }, HARD_RESET_TIMEOUT_MS)
    }

    if (
      !devMode &&
      pixelStreaming &&
      !webSocketHandlerMonkeyPatchedRef.current
    ) {
      // Wait for websocket to be ready before sending auth, re-sending auth
      // after a disconnect/reconnect cycle
      //
      // This is disabled in dev mode, since it breaks the signalling server on
      // windows VMs we use for development.
      //
      // We monkey patch `signallingProtocol.transport.handleOnOpen` to send
      // auth when the websocket connection opens.
      const transport = pixelStreaming.signallingProtocol
        .transport as WebSocketTransportInternal
      const prevOnOpen = transport.handleOnOpen.bind(transport)
      transport.handleOnOpen = () => {
        prevOnOpen()
        if (!loadingSequenceRef.current) {
          loadingSequenceRef.current = startLoadingSequence()
        }
        setLoadingStep('authenticating', loadingSequenceRef.current)
        setResetting(false)

        transport.sendMessage(
          JSON.stringify({
            type: 'geckoToken',
            value: authorization?.split(' ')[1],
          })
        )

        clearHardResetTimeout()
        startHardResetTimeout()
      }

      // Patch message handler to detect streamerList and handle auth failures
      const prevOnMessage = transport.handleOnMessage.bind(transport)
      transport.handleOnMessage = (e: MessageEvent) => {
        if (typeof e.data === 'string') {
          // Handle plain text authentication failures gracefully
          if (e.data === 'Authentication failed') {
            return // Don't pass to prevOnMessage as it will try to JSON.parse and crash
          }

          // Check for streamerList and subscribeFailed messages
          try {
            const parsed = JSON.parse(e.data)
            if (parsed.type === 'streamerList') {
              if (!parsed.ids || parsed.ids.length === 0) {
                setStreamersAvailable(false)
              } else {
                clearHardResetTimeout()
                setStreamersAvailable(true)
                setLoadingStep(
                  'loading-model',
                  loadingSequenceRef.current ?? undefined
                )
                loadingSequenceRef.current = null
              }
            } else if (parsed.type === 'subscribeFailed') {
              setStreamersAvailable(false)

              const failureMessage =
                typeof parsed.message === 'string' ? parsed.message : ''
              if (failureMessage.toLowerCase().includes('does not exist')) {
                console.warn(
                  'Model does not exist, requesting new VM...',
                  parsed
                )
                requestNewStreamingSession()
                return
              }

              return
            }
          } catch (error) {
            console.error('Failed to parse WebSocket message:', e.data, error)
          }
        }
        try {
          prevOnMessage(e)
        } catch (error) {
          console.error('Error in WebSocket message handler:', error)
        }
      }

      webSocketHandlerMonkeyPatchedRef.current = true
      pixelStreaming.connect()
    } else {
      // In dev mode, connect immediately without patching
      pixelStreaming.connect()
    }

    setSendKVCommand(() => {
      return (json: string | object) => {
        if (!pixelStreaming) return
        emitPixelStreamUIInteraction(pixelStreaming, json)
      }
    })

    return () => {
      clearHardResetTimeout()

      // End session tracking on cleanup (only if a session is active)
      if (hasActiveSession()) {
        endKestrelSession()
      }

      try {
        pixelStreaming.disconnect()
      } catch {
        // TODO: need to log this?
      }
      document.body.removeEventListener('mousemove', mouseListener)
      setVideoReady(false)
    }
  }, [
    messageHandlers,
    setSelectedMarkup,
    setSendKVCommand,
    mouseTrackerRef,
    setAnnotations,
    setCameraViews,
    setMeasurements,
    setPlaneIntersections,
    setInspectionAreas,
    setPrimitives,
    pixelStreamingURL,
    authorization,
    kestrelModel?.id,
    devMode,
    setVideoReady,
    setError,
    setHasTimedOut,
    setLoading,
    setLoadingStep,
    setPixelStreamingURL,
    setResetting,
    setVmDeleted,
    setVmID,
    startLoadingSequence,
    streamersAvailable,
    pixelStreaming,
  ])

  // Separate effect for initializing kestrelModel data
  // This prevents unnecessary reconnections when only the object reference changes
  useEffect(() => {
    setAnnotations(kestrelModel?.data.annotations ?? [])
    setMeasurements(kestrelModel?.data.measurements ?? [])
    setPlaneIntersections(kestrelModel?.data.planeIntersections ?? [])
    setCameraViews(kestrelModel?.data.cameraViews ?? [])
    setInspectionAreas(kestrelModel?.data.inspectionAreas ?? [])
    setPrimitives(kestrelModel?.data.primitives ?? [])
    setPhotoRotations(kestrelModel?.data.photoRotations ?? {})
  }, [
    kestrelModel?.id, // Only re-run when model ID changes
    kestrelModel?.data.annotations,
    kestrelModel?.data.measurements,
    kestrelModel?.data.planeIntersections,
    kestrelModel?.data.cameraViews,
    kestrelModel?.data.inspectionAreas,
    kestrelModel?.data.primitives,
    kestrelModel?.data.photoRotations,
    setAnnotations,
    setMeasurements,
    setPlaneIntersections,
    setCameraViews,
    setInspectionAreas,
    setPrimitives,
    setPhotoRotations,
  ])

  const { fetchedHistoricalAnnotations } =
    useHistoricalAnnotations(kestrelModel)

  useEffect(() => {
    setHistoricalAnnotations(fetchedHistoricalAnnotations)
  }, [fetchedHistoricalAnnotations, setHistoricalAnnotations])

  const {
    tagOptions: computedTagOptions,
    valueOverrides: computedValueOverrides,
    derivedTags: computedDerivedTags,
  } = useTagOptions()

  useEffect(() => {
    setTagOptions(computedTagOptions)
  }, [computedTagOptions, setTagOptions])

  useEffect(() => {
    setDerivedTags(computedDerivedTags)
  }, [computedDerivedTags, setDerivedTags])

  useEffect(() => {
    setValueOverrides(computedValueOverrides)
  }, [computedValueOverrides, setValueOverrides])

  // Separate effect for window event listeners
  useEffect(() => {
    if (!pixelStreaming || !kestrelStatus?.videoReady) return

    const handleWindowBlur = () => {
      // Send 'playerAway' command to Unreal Engine
      emitPixelStreamUIInteraction(pixelStreaming, {
        command: KestrelCommands.PLAYER_AWAY,
      })
    }

    const handleVisibilityChange = () => {
      if (document.hidden) {
        // Send 'playerAway' command to Unreal Engine
        emitPixelStreamUIInteraction(pixelStreaming, {
          command: KestrelCommands.PLAYER_AWAY,
        })
      }
    }

    window.addEventListener('blur', handleWindowBlur)
    document.addEventListener('visibilitychange', handleVisibilityChange)

    return () => {
      window.removeEventListener('blur', handleWindowBlur)
      document.removeEventListener('visibilitychange', handleVisibilityChange)
    }
  }, [pixelStreaming, kestrelStatus?.videoReady])

  useEffect(() => {
    if (!pixelStreaming || !kestrelStatus?.videoReady) return

    const handleKeyDown = (event: KeyboardEvent) => {
      if (
        event.target instanceof HTMLInputElement ||
        event.target instanceof HTMLTextAreaElement
      ) {
        return
      }

      handleLayerSwitch(event)
    }

    document.addEventListener('keydown', handleKeyDown)

    return () => {
      document.removeEventListener('keydown', handleKeyDown)
    }
  }, [pixelStreaming, kestrelStatus?.videoReady, handleLayerSwitch])

  // Emit current layer only when model layer config or inspection type changes
  useEffect(() => {
    if (!pixelStreaming || !kestrelStatus?.videoReady) return
    if (!hasDataLayersSupport) return
    if (!kestrelModel?.data?.dataLayersConfig) return
    emitCurrentLayerCommand()
  }, [
    pixelStreaming,
    kestrelStatus?.videoReady,
    hasDataLayersSupport,
    kestrelModel?.data?.dataLayersConfig,
    inspectionType,
    emitCurrentLayerCommand,
  ])

  useEffect(() => {
    if (!videoParent.current || !pixelStreaming || !kestrelStatus?.videoReady)
      return

    const handleMouseDown = (event: MouseEvent) => {
      handleTemperatureProbe(event)
    }

    const videoElement = videoParent.current
    videoElement.addEventListener('mousedown', handleMouseDown)

    return () => {
      videoElement.removeEventListener('mousedown', handleMouseDown)
    }
  }, [pixelStreaming, kestrelStatus?.videoReady, handleTemperatureProbe])

  useEffect(() => {
    if (!pixelStreaming) return
    const messageChunks = new Map<string, ChunkBuffer>()

    const processIncomingMessage = (dataMessage: string) => {
      const dataJson = parseMessage(dataMessage)

      if (dataJson) {
        // Check if this is a chunked message
        if (dataJson.__chunkType === 'partial' && dataJson.__messageId) {
          const totalChunks = Number(dataJson.__totalChunks)
          const chunkIndex = Number(dataJson.__chunkIndex)
          const completeMessage = processChunkedMessage(
            dataJson.__messageId,
            messageChunks,
            {
              __totalChunks: totalChunks,
              __chunkIndex: chunkIndex,
              __chunkData: dataJson.__chunkData as string,
            }
          )

          if (completeMessage) {
            try {
              const completeJson = JSON.parse(completeMessage) as ParsedMessage
              addMessage(completeJson)
              handleMessageWithHandlers(
                completeJson,
                messageHandlers as MessageHandler[],
                kestrelViewerAPI
              )
            } catch (error) {
              console.error('Error parsing reassembled message:', error)
            }
          }
        } else {
          addMessage(dataJson)
          handleMessageWithHandlers(
            dataJson,
            messageHandlers as MessageHandler[],
            kestrelViewerAPI
          )
        }
      }
    }

    pixelStreaming.addResponseEventListener(
      'kestrelResponse',
      processIncomingMessage
    )

    const webRtcController =
      pixelStreaming.webRtcController as WebRtcControllerWithPrev
    webRtcController.handleOnMessage = (ev: MessageEvent) => {
      const dec = new TextDecoder('utf-8')
      const dataMessage = dec.decode(ev.data).replaceAll('\x00', '')
      processIncomingMessage(dataMessage)

      webRtcController.prevOnMessage?.(ev)
    }

    return () => {
      pixelStreaming.removeResponseEventListener('kestrelResponse')
    }
  }, [pixelStreaming, kestrelViewerAPI, messageHandlers, addMessage])

  const sendCommand = useMemo(() => {
    return (json: string | object) => {
      if (!pixelStreaming) return
      emitPixelStreamUIInteraction(pixelStreaming, json)
    }
  }, [pixelStreaming])

  const onVideoInitialized = useCallback(() => {
    if (!pixelStreaming) return

    // Only initialize once per session; reset happens when we setVideoReady(false)
    if (kestrelStatus?.videoReady) {
      return
    }

    if (loadingSequenceRef.current !== null) {
      setLoadingStep('initializing-scene', loadingSequenceRef.current)
      loadingSequenceRef.current = null
    }

    initializationCommands({
      pixelStreaming,
      kestrelModel: kestrelModel ?? null,
      historicalAnnotations,
      gatekeeper,
      hasDataLayersSupport,
      thermalGradientVisible,
      navigationMode,
      selectedMode,
      measurementType,
      is2DPlaneModeActive,
      currentUnit,
      settings,
      authToken: authorization?.split(' ')[1],
      rolodexUrl: ROLODEX_URL,
      kestrelApiUrl: KESTREL_API_URL,
      userEmail: user.email,
      userName: user.name,
      inspectionAreas,
    })

    // Set video ready to true
    setVideoReady(true)

    // Start session tracking now that video is ready
    const inspectableSqft = calculateInspectableSqft(
      kestrelModel?.data.inspectionAreas
    )

    // Initialize metrics storage for Rolodex persistence
    const authToken = authorization?.split(' ')[1]
    if (authToken && kestrelModel?.id && ROLODEX_URL) {
      initializeMetricsStorage(ROLODEX_URL, authToken, kestrelModel.id, {
        organization: kestrelModel.tags.organization,
        site: kestrelModel.tags.site,
      })
    }

    // Session end callback to save metrics to Rolodex
    const handleSessionEnd = (metrics: {
      userEmail: string
      inspectableSqft: number | null
      sessionDurationMs: number
      activeTimeMs: number
      annotationsCreated: number
      measurementsCreated: number
      reportsGenerated: number
      dataValidationTotalTimeMs: number
      dataValidationTimeToExhaustMs: number | null
      dataValidationAnnotationCount: number | null
      reportExtensionTotalTimeMs: number
      reportTextToGenerateMs: number | null
    }) => {
      const sessionMetrics = createSessionMetrics(metrics)
      saveSessionMetricsToRolodex(sessionMetrics)
      cleanupMetricsStorage()
    }

    startKestrelSession(
      {
        vmId: kestrelStatus?.vmID ?? null,
        kestrelModelId: kestrelModel?.id ?? '',
        userEmail: user.email ?? '',
        inspectionType: inspectionType,
        inspectableSqft: inspectableSqft ?? undefined,
        unitId: kestrelModel?.tags.unit,
      },
      handleSessionEnd
    )

    startWebRTCStatsCollection(pixelStreaming)
  }, [
    pixelStreaming,
    setVideoReady,
    kestrelModel,
    kestrelStatus?.videoReady,
    kestrelStatus?.vmID,
    thermalGradientVisible,
    navigationMode,
    selectedMode,
    measurementType,
    is2DPlaneModeActive,
    settings,
    gatekeeper,
    historicalAnnotations,
    currentUnit,
    authorization,
    ROLODEX_URL,
    KESTREL_API_URL,
    user.email,
    user.name,
    setLoadingStep,
    hasDataLayersSupport,
    inspectionType,
    inspectionAreas,
  ])

  useEffect(() => {
    if (!pixelStreaming || !videoParent.current) return

    pixelStreaming.addEventListener('videoInitialized', onVideoInitialized)
    return () => {
      pixelStreaming.removeEventListener('videoInitialized', onVideoInitialized)
    }
  }, [pixelStreaming, onVideoInitialized])

  const updateAnnotation = useMemo(
    () =>
      (
        annotation: KVAnnotation,
        jsonFields: string | string[],
        setter: (a: KVAnnotation) => KVAnnotation,
        v: unknown | unknown[]
      ) => {
        updateMarkup({
          markup: annotation,
          jsonFields,
          setter,
          v,
          commandType:
            annotation.geometry?.kind === 'point'
              ? KestrelCommands.UPDATE_ANNOTATION
              : KestrelCommands.UPDATE_SURFACE_AREA,
          markupArray: annotations,
          setMarkupArray: setAnnotations,
          rolodexField: 'annotations',
          toJSON: KVAnnotation.toJSON,
          sendCommand,
          selectedMarkup,
          setSelectedMarkup,
          kestrelModelId: kestrelModel?.id ?? '',
          rolodexPatch,
        })
      },
    [
      selectedMarkup,
      setSelectedMarkup,
      sendCommand,
      annotations,
      setAnnotations,
      kestrelModel,
      rolodexPatch,
    ]
  )

  const updateHistoricalAnnotation = useMemo(
    () =>
      (
        annotation: KVAnnotation,
        jsonFields: string | string[],
        setter: (a: KVAnnotation) => KVAnnotation,
        v: unknown | unknown[]
      ) => {
        updateMarkup({
          markup: annotation,
          jsonFields,
          setter,
          v,
          commandType:
            annotation.geometry?.kind === 'point'
              ? KestrelCommands.UPDATE_ANNOTATION
              : KestrelCommands.UPDATE_SURFACE_AREA,
          markupArray: annotations,
          setMarkupArray: setHistoricalAnnotations,
          toJSON: KVAnnotation.toJSON,
          sendCommand,
          selectedMarkup,
          setSelectedMarkup,
          kestrelModelId: kestrelModel?.id ?? '',
        })
      },
    [
      selectedMarkup,
      setSelectedMarkup,
      sendCommand,
      annotations,
      setHistoricalAnnotations,
      kestrelModel,
    ]
  )

  const updateMeasurement = useMemo(
    () =>
      (
        measurement: KVMeasurement,
        jsonFields: string | string[],
        setter: (a: KVMeasurement) => KVMeasurement,
        v: string | boolean | (string | boolean)[]
      ) => {
        updateMarkup({
          markup: measurement,
          jsonFields,
          setter,
          v,
          commandType: KestrelCommands.UPDATE_MEASUREMENT,
          markupArray: measurements,
          setMarkupArray: setMeasurements,
          toJSON: KVMeasurement.toJSON,
          sendCommand,
          selectedMarkup,
          setSelectedMarkup,
          kestrelModelId: kestrelModel?.id ?? '',
          rolodexPatch,
        })
      },
    [
      selectedMarkup,
      setSelectedMarkup,
      sendCommand,
      measurements,
      setMeasurements,
      kestrelModel,
      rolodexPatch,
    ]
  )

  return (
    <Container ref={containerRef}>
      {/* Necessary to nest the video parent as its styles are overwritten */}
      <div ref={videoParent}></div>
      <KestrelViewerProvider
        value={{
          sendCommand,
          recentMessages,
          selectedMode,
          setSelectedMode,
          selectedMarkup,
          setSelectedMarkup,
          popupMenuStartPosition,
          updateAnnotation,
          updateHistoricalAnnotation,
          updateMeasurement,
          helperTextVisible,
          setHelperTextVisible,
          inspectionGridSettingsOpen,
          setInspectionGridSettingsOpen,
          settingsOpen,
          setSettingsOpen,
          infoOpen,
          setInfoOpen,
          settings,
          setSettings,
          inspectionGridSettings,
          setInspectionGridSettings,
          is2DPlaneModeActive,
          setIs2DPlaneModeActive,
          planePosition,
          setPlanePosition,
          planeRotation,
          setPlaneRotation,
          measurementType,
          setMeasurementType,
          planeIntersections,
          setPlaneIntersections,
          cameraViews,
          setCameraViews,
          primitives,
          setPrimitives,
          photoReelOpen,
          setPhotoReelOpen,
          selectedPhoto,
          setSelectedPhoto,
          photos,
          setPhotos,
          filteredPhotos,
          setFilteredPhotos,
          photoRotations,
          getPhotoRotation,
          setPhotoRotation,
          savePhotoRotations,
          photosLoading,
          photosError,
          containerRef,
          kestrelModel,
          pixelStreamingURL,
          setPixelStreamingURL,
          setSendKVCommand,
          setPopupMenuStartPosition,
          navigationMode,
          setNavigationMode,
          cameraPoses,
          setCameraPoses,
          filteringPhotos,
          setFilteringPhotos,
          resolutionQuality,
          setResolutionQuality,
          thermalGradientVisible,
          setThermalGradientVisible,
          activeLayer,
          setActiveLayer,
          availableLayerPrefixes,
          setAvailableLayerPrefixes,
          dataLayersConfig: kestrelModel?.data.dataLayersConfig,
          currentTemperature,
          setCurrentTemperature,
          temperatureProbeLoading,
          setTemperatureProbeLoading,
          screenshotResult,
          setScreenshotResult,
          authorizedAxios,
          kestrelApiUrl: KESTREL_API_URL ?? '',
          currentUnit,
          setCurrentUnit,
          dataValidationPanelOpen,
          setDataValidationPanelOpen,
          textSegmentationPopoverVisible,
          setTextSegmentationPopoverVisible,
          updateInspectionArea,
          updatePrimitive,
          onTextSegmentationComplete: (callback: () => void) => {
            textSegmentationCompleteCallbackRef.current = callback
          },
        }}>
        {showTutorial && kestrelStatus?.videoReady && <KestrelTutorial />}
        {photoReelOpen && <PhotoReelCarousel />}
        <KestrelViewerUI />
      </KestrelViewerProvider>
    </Container>
  )
}
